/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import './insured-entity-grid.scss';

import {
  Box,
  Typography,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  // InputAdornment,
  Button,
  Autocomplete,
} from '@mui/material';
import { Cancel, FilterList, ExpandMore } from '@mui/icons-material';
import {
  useGetInsuredNames,
  useGetIndustrySectorsPagedQuery,
  useDebounce,
  toPagination,
} from '@converge/graphql';
import {
  CheckedState,
  FilterState,
  Option,
} from '../../../presentation/components/table-filter';
import { useInfiniteScrollFilter } from '../../../hooks';
import { highlightTextForFilter } from '../utils';

import React, { useState, useEffect } from 'react';
import { NewConvergeButton } from '../../../presentation';
// import { highlightText } from '../claim/claim-entity.detail.notes';

const defaultFilters: CheckedState = {
  insuredName: [],
  industrySector: [],
  status: [],
  // revenueFrom: [],
  // revenueTo: [],
};

const statusOptions: Option[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'OUT_OF_BUSINESS' },
  { label: 'ERP', value: 'ERP' },
];

interface InsuredFilterProps {
  open: boolean;
  currentFilters: CheckedState;
  setCurrentFilters: (filters: CheckedState) => void;
  currentFilterLabels: CheckedState;
  setCurrentFilterLabels: (filters: CheckedState) => void;
  filterLabels: CheckedState;
  setFilterLabels: (filters: CheckedState) => void;
  resetPageNumber: () => void;
}

const InsuredFilter = ({
  open,
  currentFilters,
  setCurrentFilters,
  currentFilterLabels,
  setCurrentFilterLabels,
  filterLabels,
  setFilterLabels,
  resetPageNumber,
}: InsuredFilterProps) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [pendingFilters, setPendingFilters] =
    useState<CheckedState>(currentFilters);
  const [pendingFilterLabels, setPendingFilterLabels] =
    useState<CheckedState>(currentFilterLabels);

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    insuredName: '',
    industrySector: '',
    status: '',
  });

  const [expandedPanels, setExpandedPanels] = useState<{
    [key: string]: boolean;
  }>({
    insuredName: false,
    // revenue: false,
    industrySector: false,
    status: false,
  });

  const debouncedInsuredName = useDebounce<string>(
    searchFilters['insuredName'],
    300
  );
  const debouncedIndustrySector = useDebounce<string>(
    searchFilters['industrySector'],
    300
  );

  // --- Insured: backend search + infinite scroll via fetchMore (same as claim filter) ---
  const [insuredPage, setInsuredPage] = useState(0);

  useEffect(() => {
    setInsuredPage(0);
  }, [debouncedInsuredName]);

  const {
    loading: insuredListLoading,
    hasNextPage: hasMoreInsureds,
    fetchMore: fetchMoreInsureds,
    data: insuredData,
  } = useGetInsuredNames({
    pageNumber: insuredPage,
    pageSize: 50,
    enabled: modalOpen,
    insuredName: debouncedInsuredName,
    submissionFinalized: true,
  });

  const {
    options: allInsuredsOptions,
    loadingMore: loadingMoreInsureds,
    handleScroll: handleInsuredScroll,
  } = useInfiniteScrollFilter({
    data: insuredData,
    loading: insuredListLoading,
    hasNextPage: hasMoreInsureds,
    fetchMore: fetchMoreInsureds,
    transformData: (data) => {
      if (!data?.entities?.edges) return [];
      const opts: Option[] = [];
      data.entities.edges.forEach((edge: any) => {
        const entity = edge.node;
        if (!entity?.uuid) return;
        const label = entity.aliases?.edges?.find((a: any) => a.node.primary)
          ?.node?.name;
        if (label) opts.push({ value: entity.uuid, label });
      });
      return opts;
    },
    searchTerm: debouncedInsuredName,
    selectedValues: pendingFilters['insuredName'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: toPagination(nextPage, pageSize),
      sort: { key: 'PRIMARY_LEGAL_NAME', order: 'ASC' },
      filters: {
        submissionFinalized: true,
        contains: true,
        ...(searchTerm && { alias: searchTerm }),
      },
    }),
  });
  // --- End insured ---

  // --- Industry sector: backend search + infinite scroll (same pattern as insured) ---
  const [sectorPage, setSectorPage] = useState(0);

  useEffect(() => {
    setSectorPage(0);
  }, [debouncedIndustrySector]);

  const {
    loading: sectorListLoading,
    hasNextPage: hasMoreSectors,
    fetchMore: fetchMoreSectors,
    data: sectorData,
  } = useGetIndustrySectorsPagedQuery({
    pageNumber: sectorPage,
    pageSize: 50,
    enabled: modalOpen,
    name: debouncedIndustrySector,
  });

  const {
    options: allSectorOptions,
    loadingMore: loadingMoreSectors,
    handleScroll: handleSectorScroll,
  } = useInfiniteScrollFilter({
    data: sectorData,
    loading: sectorListLoading,
    hasNextPage: hasMoreSectors,
    fetchMore: fetchMoreSectors,
    transformData: (data) => {
      if (!data?.industrySectors?.edges) return [];
      const opts: Option[] = [];
      data.industrySectors.edges.forEach((edge: any) => {
        if (!edge.node?.uuid || !edge.node?.name) return;
        opts.push({ value: edge.node.uuid, label: edge.node.name });
      });
      return opts;
    },
    searchTerm: debouncedIndustrySector,
    selectedValues: pendingFilters['industrySector'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: { after: nextPage * pageSize, first: pageSize },
      filters: { name: searchTerm || null },
    }),
  });
  // --- End sector ---

  // const handleRevenueChange = (
  //   field: 'revenueFrom' | 'revenueTo',
  //   value: string
  // ) => {
  //   const numericValue = value.replace(/[^0-9.]/g, '');
  //   setPendingFilters({
  //     ...pendingFilters,
  //     [field]: numericValue ? [numericValue] : [],
  //   });
  // };

  // const revenueFromVal = pendingFilters['revenueFrom']?.[0] || '';
  // const revenueToVal = pendingFilters['revenueTo']?.[0] || '';
  // const revenueFromGreaterError =
  //   !!(revenueFromVal && revenueToVal) &&
  //   parseFloat(revenueFromVal) > parseFloat(revenueToVal);
  // const revenueToMissingError = !!(revenueFromVal && !revenueToVal);
  // const revenueFromMissingError = !!(!revenueFromVal && revenueToVal);
  // const revenueError =
  //   revenueFromGreaterError || revenueToMissingError || revenueFromMissingError;

  const handleModalOpen = () => {
    setModalOpen(true);
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
    setSearchFilters({ insuredName: '', industrySector: '', status: '' });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSearchFilters({ insuredName: '', industrySector: '', status: '' });
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
  };

  const handleAccordionChange = (panel: string) => {
    setExpandedPanels({ ...expandedPanels, [panel]: !expandedPanels[panel] });
  };

  const handleCheckboxChange = (column: string, option: Option) => {
    const currentChecked = Array.isArray(pendingFilters[column])
      ? pendingFilters[column]
      : [];
    const currentLabels = Array.isArray(pendingFilterLabels[column])
      ? pendingFilterLabels[column]
      : [];

    let updatedFilter: string[];
    let updatedFilterLabel: string[];

    if (currentChecked.includes(option.value)) {
      updatedFilter = currentChecked.filter((item) => item !== option.value);
      updatedFilterLabel = currentLabels.filter(
        (label) => label !== option.label
      );
    } else {
      updatedFilter = [...currentChecked, option.value];
      updatedFilterLabel = [...currentLabels, option.label];
    }

    setPendingFilters({ ...pendingFilters, [column]: updatedFilter });
    setPendingFilterLabels({
      ...pendingFilterLabels,
      [column]: updatedFilterLabel,
    });
  };

  const handleSaveFilter = () => {
    setCurrentFilters(pendingFilters);
    setCurrentFilterLabels(pendingFilterLabels);
    setFilterLabels(pendingFilterLabels);
    localStorage.setItem('insuredFilters', JSON.stringify(pendingFilters));
    localStorage.setItem(
      'insuredFilterLabels',
      JSON.stringify(pendingFilterLabels)
    );
    resetPageNumber();
    handleModalClose();
  };

  const handleClearAllFilters = () => {
    setCurrentFilters(defaultFilters);
    setCurrentFilterLabels(defaultFilters);
    setFilterLabels(defaultFilters);
    setPendingFilters(defaultFilters);
    setPendingFilterLabels(defaultFilters);
    localStorage.removeItem('insuredFilters');
    localStorage.removeItem('insuredFilterLabels');
    resetPageNumber();
  };

  const renderFilterAccordion = (
    column: string,
    label: string,
    options: Option[],
    scrollHandler?: (e: React.UIEvent<HTMLDivElement>) => void,
    loadingMore = false
  ) => {
    const selectedCount = pendingFilters[column]?.length || 0;
    const displayLabel =
      selectedCount > 0 ? `${label} (${selectedCount})` : label;

    return (
      <Accordion
        expanded={expandedPanels[column]}
        onChange={() => handleAccordionChange(column)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': { margin: '0 !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            minHeight: '56px',
            height: '56px',
            '& .MuiAccordionSummary-content': { margin: '0' },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ marginLeft: '38px', mb: 2, width: '85%' }}>
            <Autocomplete
              size="small"
              fullWidth
              multiple
              freeSolo={true}
              filterOptions={(x) => x}
              options={[]}
              open={false}
              getOptionLabel={(option): string => {
                if (typeof option === 'string') return option;
                if (option && typeof option === 'object' && 'label' in option) {
                  return String(option.label || '');
                }
                return '';
              }}
              value={[] as Option[]}
              isOptionEqualToValue={(
                option: Option | string,
                value: Option | string
              ) => {
                if (!option || !value) return false;
                if (typeof option === 'string' || typeof value === 'string')
                  return false;
                return (option as Option).value === (value as Option).value;
              }}
              onChange={() => {}}
              inputValue={searchFilters[column]}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'reset') return;
                if (reason === 'clear') {
                  if (scrollHandler) return;
                  setSearchFilters({ ...searchFilters, [column]: '' });
                  return;
                }
                setSearchFilters({ ...searchFilters, [column]: newInputValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Search ${label}`}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      fontWeight: '600',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'black',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: '200px' } }}
              sx={{
                '& .MuiAutocomplete-popupIndicator': { display: 'none' },
                '& .MuiAutocomplete-clearIndicator': {
                  display: scrollHandler ? 'none' : 'block',
                  visibility: 'visible',
                  color: 'black',
                },
              }}
            />
          </Box>
          <Box
            sx={{
              marginLeft: '30px',
              width: '85%',
              maxHeight: '250px',
              overflow: 'auto',
            }}
            onScroll={scrollHandler}
          >
            <FormGroup>
              {options.length > 0 ? (
                options.map((option, index) => {
                  const isChecked = pendingFilters[column]?.includes(
                    option.value
                  );

                  return (
                    <FormControlLabel
                      key={`${option.value}-${index}`}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(column, option)}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            paddingTop: '4px',
                            color: '#666666',
                            '&.Mui-checked': {
                              color: '#28C0A7',
                            },
                          }}
                        />
                      }
                      label={highlightTextForFilter(
                        option.label,
                        searchFilters[column]
                      )}
                      sx={{
                        alignItems: 'flex-start',
                        margin: '2px 0',
                        '& .MuiFormControlLabel-label': {
                          fontWeight: '600',
                          fontSize: '14px',
                          paddingTop: '3px',
                        },
                      }}
                    />
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: '#474343', marginLeft: '10px' }}
                >
                  No results
                </Typography>
              )}
              {loadingMore && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
            </FormGroup>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  // const renderRevenueAccordion = () => {
  //   const hasRevenue = !!(
  //     pendingFilters['revenueFrom']?.[0] || pendingFilters['revenueTo']?.[0]
  //   );
  //   const displayLabel = hasRevenue ? 'Current Revenue (1)' : 'Current Revenue';
  //   return (
  //     <Accordion
  //       expanded={expandedPanels['revenue']}
  //       onChange={() => handleAccordionChange('revenue')}
  //     >
  //       ...
  //     </Accordion>
  //   );
  // };

  const renderActiveFilters = () => {
    // const revenueFrom = currentFilters['revenueFrom']?.[0] || '';
    // const revenueTo = currentFilters['revenueTo']?.[0] || '';
    // const hasRevenue = !!(revenueFrom || revenueTo);

    const otherActiveFilters = Object.keys(currentFilterLabels).filter(
      (key) =>
        key !== 'revenueFrom' &&
        key !== 'revenueTo' &&
        currentFilterLabels[key]?.length > 0
    );

    if (otherActiveFilters.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
        {/* {hasRevenue && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: '22px' }}>
            ...revenue chip...
          </Box>
        )} */}
        {otherActiveFilters.map((filterKey) => {
          const labels = currentFilterLabels[filterKey] || [];
          return labels.map((label: string, index: number) => (
            <Box
              key={`${filterKey}-${index}`}
              sx={{ display: 'flex', alignItems: 'center', mr: '22px' }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: '14px' }}
              >
                {label}
              </Typography>
              <Cancel
                sx={{ ml: 1, fontSize: '16px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const labelIndex = (
                    currentFilterLabels[filterKey] || []
                  ).indexOf(label);
                  const valueToRemove =
                    labelIndex !== -1
                      ? (currentFilters[filterKey] || [])[labelIndex]
                      : undefined;

                  if (valueToRemove) {
                    const updatedFilters = {
                      ...currentFilters,
                      [filterKey]: (currentFilters[filterKey] || []).filter(
                        (v) => v !== valueToRemove
                      ),
                    };
                    const updatedLabels = {
                      ...currentFilterLabels,
                      [filterKey]: (
                        currentFilterLabels[filterKey] || []
                      ).filter((l) => l !== label),
                    };
                    setCurrentFilters(updatedFilters);
                    setCurrentFilterLabels(updatedLabels);
                    setFilterLabels(updatedLabels);
                    setPendingFilters(updatedFilters);
                    setPendingFilterLabels(updatedLabels);
                    localStorage.setItem(
                      'insuredFilters',
                      JSON.stringify(updatedFilters)
                    );
                    localStorage.setItem(
                      'insuredFilterLabels',
                      JSON.stringify(updatedLabels)
                    );
                    resetPageNumber();
                  }
                }}
              />
            </Box>
          ));
        })}
      </Box>
    );
  };

  return (
    <>
      <Box
        className="filter-buttons"
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '10px',
          flexWrap: 'wrap',
          color: 'black',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px solid black',
            borderRadius: '4px',
            marginRight: '10px',
          }}
          onClick={handleModalOpen}
        >
          <FilterList sx={{ padding: '5px 5px' }} />
        </Box>

        {renderActiveFilters()}
        {Object.keys(currentFilterLabels).some(
          (key) => currentFilterLabels[key]?.length > 0
        ) && (
          <Button
            size="small"
            onClick={handleClearAllFilters}
            disableRipple
            sx={{
              ml: 0,
              textAlign: 'left',
              color: 'black',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: 'transparent',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:focus': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:active': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&.Mui-focusVisible': { backgroundColor: 'transparent' },
            }}
          >
            Clear all filters
          </Button>
        )}
      </Box>

      <Drawer anchor="left" open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            height: '100vh',
            width: 400,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {renderFilterAccordion(
              'insuredName',
              'Insured Name',
              allInsuredsOptions,
              handleInsuredScroll,
              loadingMoreInsureds
            )}
            {/* {renderRevenueAccordion()} */}
            {renderFilterAccordion(
              'industrySector',
              'Industry Sector',
              allSectorOptions,
              handleSectorScroll,
              loadingMoreSectors
            )}
            {renderFilterAccordion('status', 'Status', statusOptions)}
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: 3,
              height: '39px',
            }}
          >
            <Box style={{ marginLeft: '50px' }}>
              <NewConvergeButton
                variant="outlined"
                label={' Cancel'}
                onClick={handleModalClose}
              />
            </Box>
            <Box sx={{ width: '137px' }}>
              <NewConvergeButton
                variant="contained"
                onClick={handleSaveFilter}
                label=" Save Filters"
                disabled={false}
              />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default InsuredFilter;
